/**
 * "Manual Actions" bucket card for the Action required group.
 * The card row shows a ReturnGO badge icon, label, total count (always visible, even at 0) and a chevron.
 * Clicking it opens a floating popup of sections; picking sections adds removable chips to the card.
 * Manual actions are mutually exclusive with the other filters and apply immediately on change.
 */
import { observer } from "mobx-react";
import "./rma-list-manual-actions-bucket.scss";
import useDashboardTranslation from "../../../../../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { MANUAL_ACTION_TYPE } from "../../../../../../../../../services/api/rma-list-service";
import { RmaListBucketFiltersStore } from "../../../../../../../rma-list-page/stores/rma-list-bucket-filters-store";
import RmaListManualActionsPopup from "./components/rma-list-manual-actions-popup/rma-list-manual-actions-popup";
import RmaListManualActionsBucketCardRow from "./components/rma-list-manual-actions-bucket-card-row/rma-list-manual-actions-bucket-card-row";
import RmaListManualActionsBucketChips from "./components/rma-list-manual-actions-bucket-chips/rma-list-manual-actions-bucket-chips";
import useBucketPopup from "../../../../../../hooks/use-bucket-popup";

interface IProps {
    bucketFiltersStore: RmaListBucketFiltersStore;
    disabled: boolean;
    onApply: () => void;
}

/** Maps a manual-action bucket value to its dashboard-translation key (scope: dashboard-returns-page). */
const MANUAL_ACTION_LABEL_KEY: Record<string, string> = {
    [MANUAL_ACTION_TYPE.APPROVE]: "MANUAL_ACTION_APPROVE",
    [MANUAL_ACTION_TYPE.MANAGER_APPROVAL]: "MANUAL_ACTION_MANAGER_APPROVAL",
    [MANUAL_ACTION_TYPE.RECEIVE]: "MANUAL_ACTION_RECEIVE",
    [MANUAL_ACTION_TYPE.REFUND]: "MANUAL_ACTION_REFUND",
    [MANUAL_ACTION_TYPE.RELEASE_EXCHANGE]: "MANUAL_ACTION_RELEASE_EXCHANGE",
    [MANUAL_ACTION_TYPE.VALIDATE]: "MANUAL_ACTION_VALIDATE",
    [MANUAL_ACTION_TYPE.RESTOCK]: "MANUAL_ACTION_RESTOCK",
    [MANUAL_ACTION_TYPE.MARK_DONE]: "MANUAL_ACTION_MARK_DONE",
};

const RmaListManualActionsBucket: React.FC<IProps> = observer(({ bucketFiltersStore, disabled, onApply }) => {
    const { getTranslation } = useDashboardTranslation();
    const scope = "dashboard-returns-page";
    const { isOpen, refs, floatingStyles, getReferenceProps, getFloatingProps } = useBucketPopup(disabled);

    const selected = bucketFiltersStore.selectedManualActions;
    const hasSelection = selected.length > 0;

    const getLabel = (value: string) => getTranslation(MANUAL_ACTION_LABEL_KEY[value] ?? value, scope);
    const getCount = (value: string) =>
        bucketFiltersStore.manualActionOptions.find((option) => option.value === value)?.count ?? 0;

    const toggleAction = (value: string) => {
        bucketFiltersStore.toggleManualAction(value);
        onApply();
    };

    const toggleAllActions = () => {
        bucketFiltersStore.toggleAllManualActions();
        onApply();
    };

    const cardActive = isOpen || hasSelection;
    // Empty bucket (no open RMAs needing a manual action) stays visible but dimmed, unless the merchant
    // is actively interacting with it (open / has a selection).
    const isEmpty = bucketFiltersStore.manualActionsTotal === 0 && !cardActive;

    return (
        <div className="rma-list-manual-actions-bucket">
            <div className={`rma-list-manual-actions-bucket__card${cardActive ? " active" : ""}${isEmpty ? " empty" : ""}${disabled ? " disabled" : ""}`}>
                <RmaListManualActionsBucketCardRow
                    label={getTranslation("MANUAL_ACTIONS", scope)}
                    total={bucketFiltersStore.manualActionsTotal}
                    loading={bucketFiltersStore.areCountersLoading}
                    open={isOpen}
                    disabled={disabled}
                    triggerRef={refs.setReference}
                    triggerProps={getReferenceProps()}
                />

                {hasSelection && (
                    <RmaListManualActionsBucketChips
                        selected={selected}
                        getLabel={getLabel}
                        getCount={getCount}
                        disabled={disabled}
                        onRemove={toggleAction}
                    />
                )}
            </div>

            {isOpen && (
                <div
                    ref={refs.setFloating}
                    style={floatingStyles}
                    className="rma-list-manual-actions-bucket__popup"
                    {...getFloatingProps()}
                >
                    <RmaListManualActionsPopup
                        bucketFiltersStore={bucketFiltersStore}
                        getLabel={getLabel}
                        onToggle={toggleAction}
                        onToggleAll={toggleAllActions}
                    />
                </div>
            )}
        </div>
    );
});

export default RmaListManualActionsBucket;
